import type { QuizKind } from "@rmnlng/shared";
import { BookMarked, Check, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import { useGradePracticeAnswer, type QuizPracticeCard } from "@/lib/practice";
import { useAdvanceQuizCard, useSubmitQuiz, type QuizReviewCard } from "@/lib/reviews";
import { cn } from "@/lib/utils";

// ─── Quiz card (CLOZE + TRANSFORM) ─────────────────────────────────
//
// Shared rendering surface used by:
//   - The SRS Study queue (`<QuizCardView>` in `Study.tsx`)
//   - The per-rule Practice drill (`<PracticeSession>`)
//
// Both consumers provide their own `QuizFlow` (the state +
// submit/advance handlers). The surface itself only renders — it
// doesn't know whether the answer goes to /reviews or
// /grammar/:id/practice/answer.
//
// Auto-advance timing on correct answers is owned by the flow hooks
// (different semantics in each context) — see `useQuizSubmitFlow`
// and `usePracticeFlow` below.

const QUIZ_CORRECT_DWELL_MS = 1500;

// Structural minimum the rendering components need. `QuizReviewCard`
// (SRS) has `quizItemId / ruleId / state / preview / isNew` extras;
// `QuizPracticeCard` has just the rendering fields. Both satisfy this
// type.
export type QuizCardForRender = {
  quizItemId: string;
  // Optional — the SRS card surfaces it as a chip; practice mode hides
  // it (the rule title sits in the page-level `<QuizHeader>` instead).
  ruleTitle?: string;
  isNew?: boolean;
  quizKind: QuizKind;
  sentence: string;
  translation: string | null;
  sourceSentence: string | null;
  sourceTranslation: string | null;
  hints: string[];
  explanation: string | null;
  options?: string[];
  mode: "mcq" | "type";
};

// Common shape returned by both flow hooks. The surface components
// read these fields; whoever builds the flow controls the actual
// submit + advance semantics.
export type QuizFlow = {
  isMcq: boolean;
  userAnswer: string;
  hasAnswer: boolean;
  submitted: boolean;
  result: { correct: boolean; expectedAnswers: string[] } | null;
  selectedOption: string | null;
  setSelectedOption: (s: string | null) => void;
  typedInput: string;
  setTypedInput: (s: string) => void;
  isSubmitting: boolean;
  handleSubmit: (answer: string) => void;
  handleContinue: () => void;
};

// Flow internal state — extracted so both `useQuizSubmitFlow` and
// `usePracticeFlow` reuse the same hooks/local-state setup. They
// differ only in what `handleSubmit` and `handleContinue` do.
function useQuizFlowState(card: QuizCardForRender) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [typedInput, setTypedInput] = useState("");
  const [result, setResult] = useState<{ correct: boolean; expectedAnswers: string[] } | null>(null);

  // Per-card timer for `secondsSpent`. Component remounts on card
  // change (key={quizItemId} on parent), so `useMemo([])` captures the
  // wall-clock from the moment the user first sees the prompt.
  const startedAt = useMemo(() => performance.now(), []);

  const isMcq = card.mode === "mcq";
  const userAnswer = isMcq ? (selectedOption ?? "") : typedInput;
  const hasAnswer = userAnswer.trim().length > 0;
  const submitted = result !== null;

  return {
    isMcq,
    userAnswer,
    hasAnswer,
    submitted,
    result,
    setResult,
    selectedOption,
    setSelectedOption,
    typedInput,
    setTypedInput,
    startedAt,
  };
}

/**
 * SRS-aware flow for the Study queue. Submits to `/reviews` (writing
 * QuizProgress + ReviewLog), then on correct answers triggers an
 * auto-advance dwell + queue-cache eviction. On wrong answers the
 * user dismisses the result panel manually via `handleContinue`.
 */
export function useQuizSubmitFlow(card: QuizReviewCard): QuizFlow {
  const submit = useSubmitQuiz();
  const advance = useAdvanceQuizCard();
  const state = useQuizFlowState(card);

  function handleSubmit(answer: string) {
    if (state.submitted || submit.isPending) return;
    const seconds = Math.max(0, Math.round((performance.now() - state.startedAt) / 1000));
    submit.mutate(
      { quizItemId: card.quizItemId, userAnswer: answer, secondsSpent: seconds },
      {
        onSuccess: (response) => {
          state.setResult({ correct: response.correct, expectedAnswers: response.expectedAnswers });
          if (response.correct) {
            // Auto-advance after the dwell. Component will unmount on
            // cache update, so the timer ID doesn't need cleanup.
            window.setTimeout(() => advance(card.quizItemId), QUIZ_CORRECT_DWELL_MS);
          }
        },
      },
    );
  }

  function handleContinue() {
    advance(card.quizItemId);
  }

  return {
    isMcq: state.isMcq,
    userAnswer: state.userAnswer,
    hasAnswer: state.hasAnswer,
    submitted: state.submitted,
    result: state.result,
    selectedOption: state.selectedOption,
    setSelectedOption: state.setSelectedOption,
    typedInput: state.typedInput,
    setTypedInput: state.setTypedInput,
    isSubmitting: submit.isPending,
    handleSubmit,
    handleContinue,
  };
}

/**
 * Practice-mode flow for the per-rule drill. Submits to
 * `/grammar/:id/practice/answer` (no DB writes, no SRS update). On
 * advance (auto for correct, manual for wrong), notifies the parent
 * via `onComplete` so it can move to the next item in the local
 * session list.
 */
export function usePracticeFlow(card: QuizPracticeCard, opts: { ruleId: string; onComplete: () => void }): QuizFlow {
  const grade = useGradePracticeAnswer(opts.ruleId);
  const state = useQuizFlowState(card);

  function handleSubmit(answer: string) {
    if (state.submitted || grade.isPending) return;
    grade.mutate(
      { quizItemId: card.quizItemId, userAnswer: answer },
      {
        onSuccess: (response) => {
          state.setResult({ correct: response.correct, expectedAnswers: response.expectedAnswers });
          if (response.correct) {
            window.setTimeout(opts.onComplete, QUIZ_CORRECT_DWELL_MS);
          }
        },
      },
    );
  }

  return {
    isMcq: state.isMcq,
    userAnswer: state.userAnswer,
    hasAnswer: state.hasAnswer,
    submitted: state.submitted,
    result: state.result,
    selectedOption: state.selectedOption,
    setSelectedOption: state.setSelectedOption,
    typedInput: state.typedInput,
    setTypedInput: state.setTypedInput,
    isSubmitting: grade.isPending,
    handleSubmit,
    handleContinue: opts.onComplete,
  };
}

/**
 * Composition shell. Renders header + per-kind prompt + hint pills +
 * answer widget + result panel + bottom actions.
 *
 * Caller decides whether to mount with `key={quizItemId}` for state
 * reset on card change (Study.tsx + PracticeSession.tsx both do).
 */
export function QuizCardSurface({ card, flow }: { card: QuizCardForRender; flow: QuizFlow }) {
  const t = useT();

  // Reject anything we don't have a renderer for. Today both CLOZE
  // and TRANSFORM are supported; future kinds (error-correction,
  // sentence-builder) hit this fallback until their phase ships.
  if (card.quizKind !== "CLOZE" && card.quizKind !== "TRANSFORM") {
    return (
      <div className="flashcard flex-1 items-center justify-center text-center text-text-3">
        <div className="text-sm">{t("study.quiz_unsupported_kind")}</div>
      </div>
    );
  }

  return (
    <div className="flashcard flex flex-1 flex-col gap-4 overflow-y-auto">
      {card.ruleTitle && <QuizCardHeader title={card.ruleTitle} isNew={card.isNew ?? false} />}
      {card.quizKind === "TRANSFORM" ? (
        <TransformPrompt card={card} flow={flow} />
      ) : (
        <ClozePrompt card={card} flow={flow} />
      )}
      <QuizHints hints={card.hints} hide={flow.submitted} />
      {!flow.submitted && <QuizAnswerWidget card={card} flow={flow} />}
      {flow.submitted && flow.result && <QuizResultPanel result={flow.result} explanation={card.explanation} />}
      <div className="flex-1" />
      <QuizBottomActions flow={flow} />
    </div>
  );
}

// ─── Header — rule chip + NEW badge ──────────────────────────────
// Shown only when `card.ruleTitle` is set (SRS queue). Practice mode
// surfaces the rule via the page-level `<QuizHeader>` chrome instead.
function QuizCardHeader({ title, isNew }: { title: string; isNew: boolean }) {
  const t = useT();
  return (
    <div className="flex items-center justify-between">
      <Badge variant="accent" className="gap-1.5">
        <BookMarked className="size-3" /> {title}
      </Badge>
      {isNew && (
        <Badge variant="default" className="gap-1">
          <Sparkles className="size-3" /> {t("study.badge_new")}
        </Badge>
      )}
    </div>
  );
}

// ─── Sentence with the answer-blank inlined ──────────────────────
// Shared between CLOZE (the only sentence) and TRANSFORM (target
// sentence only — source sits in its own card). Visualizes the
// blank's state across the lifecycle: dashed waiting → green-bg
// (correct) → red-bg + strikethrough (wrong).
function SentenceWithBlank({ sentence, flow }: { sentence: string; flow: QuizFlow }) {
  const parts = sentence.split("___");
  const submitted = flow.submitted && flow.result !== null;
  const correct = flow.result?.correct ?? false;
  return (
    <div className="text-[20px] font-semibold leading-[1.4] tracking-[-0.01em]">
      {parts[0]}
      <span
        className={cn(
          "mx-1 inline-block min-w-[3rem] rounded px-2 align-baseline",
          submitted
            ? correct
              ? "bg-[color:var(--good-soft)] text-[color:var(--good)]"
              : "bg-[color:var(--again-soft)] text-[color:var(--again)] line-through"
            : "border-b-2 border-dashed border-text-3",
        )}
      >
        {submitted ? flow.userAnswer || "—" : "   "}
      </span>
      {parts[1] ?? ""}
    </div>
  );
}

// ─── CLOZE prompt: eyebrow + sentence + translation ──────────────
function ClozePrompt({ card, flow }: { card: QuizCardForRender; flow: QuizFlow }) {
  const t = useT();
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {flow.isMcq ? t("study.quiz_choose_eyebrow") : t("study.quiz_type_eyebrow")}
      </div>
      <div className="mt-2">
        <SentenceWithBlank sentence={card.sentence} flow={flow} />
      </div>
      {card.translation && <div className="mt-1.5 text-[13px] italic text-text-3">{card.translation}</div>}
    </div>
  );
}

// ─── TRANSFORM prompt: eyebrow + source-card → target-card ──────
function TransformPrompt({ card, flow }: { card: QuizCardForRender; flow: QuizFlow }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("study.quiz_transform_eyebrow")}
      </div>
      <div className="rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("study.quiz_transform_source")}
        </div>
        <div className="mt-1 text-[16px] font-medium leading-[1.45]">{card.sourceSentence ?? ""}</div>
        {card.sourceTranslation && (
          <div className="mt-0.5 text-[12px] italic text-text-3">{card.sourceTranslation}</div>
        )}
      </div>
      <div className="flex justify-center">
        <div
          className="flex size-7 items-center justify-center rounded-full"
          style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          aria-hidden
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14M19 12l-7 7-7-7" />
          </svg>
        </div>
      </div>
      <div className="rounded-[var(--r-md)] border-2 border-[color:var(--accent)] px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
          {t("study.quiz_transform_target")}
        </div>
        <div className="mt-1">
          <SentenceWithBlank sentence={card.sentence} flow={flow} />
        </div>
        {card.translation && <div className="mt-0.5 text-[12px] italic text-text-3">{card.translation}</div>}
      </div>
    </div>
  );
}

function QuizHints({ hints, hide }: { hints: string[]; hide: boolean }) {
  if (hide || hints.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {hints.map((h, i) => (
        <span
          key={i}
          className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] text-text-2"
        >
          {h}
        </span>
      ))}
    </div>
  );
}

function QuizAnswerWidget({ card, flow }: { card: QuizCardForRender; flow: QuizFlow }) {
  const t = useT();
  return (
    <div className="flex flex-col gap-2">
      {flow.isMcq && card.options ? (
        card.options.map((opt, i) => {
          const sel = flow.selectedOption === opt;
          const letter = String.fromCharCode(65 + i); // A, B, C, D
          return (
            <button
              key={opt}
              type="button"
              onClick={() => flow.setSelectedOption(opt)}
              className={cn(
                "flex items-center gap-3 rounded-[var(--r-md)] border-2 px-4 py-3 text-left text-[15px] transition-colors",
                sel
                  ? "border-[color:var(--accent)] bg-[color:var(--accent-soft)] font-semibold"
                  : "border-border bg-surface hover:bg-[color:var(--surface-2)]",
              )}
            >
              <span
                className={cn(
                  "flex size-[22px] shrink-0 items-center justify-center rounded font-mono text-[11px] font-bold",
                  sel
                    ? "bg-[color:var(--accent)] text-[color:var(--accent-fg)]"
                    : "bg-[color:var(--surface-2)] text-text-3",
                )}
              >
                {letter}
              </span>
              <span>{opt}</span>
            </button>
          );
        })
      ) : (
        <Input
          autoFocus
          value={flow.typedInput}
          onChange={(e) => flow.setTypedInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && flow.hasAnswer) {
              e.preventDefault();
              flow.handleSubmit(flow.userAnswer);
            }
          }}
          placeholder={t("study.quiz_input_placeholder")}
          className="text-[16px]"
        />
      )}
    </div>
  );
}

function QuizResultPanel({
  result,
  explanation,
}: {
  result: { correct: boolean; expectedAnswers: string[] };
  explanation: string | null;
}) {
  const t = useT();
  return (
    <div
      className={cn(
        "rounded-[var(--r-md)] border px-3.5 py-3 text-[13px]",
        result.correct
          ? "border-[color:var(--good)] bg-[color:var(--good-soft)] text-[color:var(--good)]"
          : "border-[color:var(--again)] bg-[color:var(--again-soft)] text-[color:var(--again)]",
      )}
    >
      <div className="flex items-center gap-2 font-semibold">
        {result.correct ? <Check className="size-4" /> : <X className="size-4" />}
        {result.correct
          ? t("study.quiz_correct_label")
          : t("study.quiz_correct_answer", { answer: result.expectedAnswers[0] ?? "" })}
      </div>
      {explanation && (
        <div className="mt-1.5 text-[13px] leading-[1.5] text-text-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{explanation}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function QuizBottomActions({ flow }: { flow: QuizFlow }) {
  const t = useT();
  if (!flow.submitted) {
    return (
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => flow.handleSubmit("")} disabled={flow.isSubmitting} className="px-4">
          {t("study.quiz_skip")}
        </Button>
        <Button
          className="flex-1"
          onClick={() => flow.handleSubmit(flow.userAnswer)}
          disabled={!flow.hasAnswer || flow.isSubmitting}
        >
          {t("study.quiz_check")}
        </Button>
      </div>
    );
  }
  // Continue button shown for wrong answers (correct ones auto-advance
  // after dwell). The button still works during the dwell window — lets
  // an impatient user skip ahead.
  if (flow.result && !flow.result.correct) {
    return (
      <Button size="block" onClick={flow.handleContinue}>
        {t("study.quiz_continue")}
      </Button>
    );
  }
  return null;
}
